/* eslint-disable react/display-name */

'use client';

import React, { memo, useMemo, useRef, forwardRef } from 'react';

import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import { isEmpty, isString } from '@wconcept/wconcept-common/utils/baseUtils';
import { sanitizeContent } from '@wconcept/wconcept-common/utils/htmlSanitizer';
import debounce from 'lodash-es/debounce';

import * as S from './style';

export interface IButtonProps {
  /** 'button' | 'submit' | 'reset' */
  type?: 'button' | 'submit' | 'reset' | undefined;
  /** href */
  href?: string;
  /** children */
  children?: React.ReactNode;
  /** label */
  label?: string;
  /** className */
  className?: string;
  /** disabled */
  disabled?: boolean;
  /** target */
  target?: string;
  /** rel */
  rel?: string;
  /** ref */
  ref?: unknown;
  /** icon */
  icon?: React.ReactNode;
  /** onClick */
  onClick?: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => Promise<void> | void;
  isDebounced?: boolean;
  /** onMouseEnter */
  onMouseEnter?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  /** onMouseLeave */
  onMouseLeave?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  /** onFocus */
  onFocus?: (e: React.FocusEvent<HTMLButtonElement, Event>) => void;
  /** onBlur */
  onBlur?: (e: React.FocusEvent<HTMLButtonElement, MouseEvent>) => void;
  /** role */
  role?: string;
  /** aria-controls */
  ariaControls?: string;
  /** aria-selected */
  ariaSelected?: boolean;
  /** aria-expanded */
  ariaExpanded?: boolean;
}

const Button = forwardRef(
  (
    {
      type = 'button',
      href,
      className,
      disabled,
      target,
      rel,
      icon,
      label,
      onClick,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      children,
      role,
      ariaControls,
      ariaSelected,
      ariaExpanded,
      isDebounced = true,
    }: IButtonProps,
    ref
  ) => {
    const btnRef = useRef<unknown>(ref || null);

    // 디바운스된 onClick 핸들러 생성
    const debouncedOnClick = useMemo(() => {
      if (!isDebounced) return onClick;

      return debounce(
        async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          e.persist(); // React 이벤트 객체를 유지
          try {
            if (onClick) {
              if (onClick instanceof Promise) {
                await onClick(e);
              } else {
                onClick(e);
              }
            }
          } catch (error) {
            console.error('Button onClick Error', error);
          }
        },
        200
      );
    }, [isDebounced, onClick]);

    const param: any = {
      href: href || undefined,
      ref: btnRef,
      className: useMemo(
        () =>
          classNameBind(['btn', disabled ? 'disabled' : '', className || '']),
        [disabled, className]
      ),
      type: href ? null : type,
      target,
      disabled,
      rel: target ? rel : null,
      $icon: icon,
      role,
      'aria-label': label,
      'aria-controls': ariaControls,
      'aria-selected': ariaSelected,
      'aria-expanded': ariaExpanded,
      onClick: debouncedOnClick,
      onMouseEnter: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (onMouseEnter) {
          onMouseEnter(e);
        }
      },
      onMouseLeave: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (onMouseLeave) {
          onMouseLeave(e);
        }
      },
      onFocus: (e: React.FocusEvent<HTMLButtonElement, Event>) => {
        if (onFocus) {
          onFocus(e);
        }
      },
    };

    // ITEM RENDERER : getChildrenRender
    const getChildrenRender = () => {
      const str = (el: React.ReactNode) => String(el);
      return (
        <>
          {!isEmpty(icon) && icon}
          {!isEmpty(children) && isString(children) ? (
            // eslint-disable-next-line react/no-danger
            <span
              dangerouslySetInnerHTML={{
                __html: sanitizeContent(str(children)),
              }}
            />
          ) : (
            children
          )}
        </>
      );
    };

    return (
      <S.Button {...param} as={href ? 'a' : 'button'}>
        {getChildrenRender()}
      </S.Button>
    );
  }
);

export default memo(Button);
